import React, { useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ExternalLink } from 'lucide-react';

/**
 * Where a punch happened, on a map.
 *
 * Attendance has stored coordinates since phase 1 and every screen printed them
 * as two decimal numbers, which nobody can read. A pin answers the question
 * somebody is actually asking — was this person anywhere near the site — and the
 * Google Maps link answers the follow-up, which is "where exactly is that".
 *
 * WHOSE LOCATION THIS IS depends on the caller and the component will not guess.
 * On a labour punch the coordinate is the SUPERVISOR's, because workmen do not
 * carry phones; on staff attendance it is the employee's own. Pass `caption` and
 * say which — a map that implies the wrong thing about who was standing where
 * has wage and compliance consequences.
 */

/**
 * Leaflet's default marker images are resolved relative to the CSS, which
 * webpack rewrites — so the stock pin 404s in a CRA build. A divIcon avoids the
 * asset problem entirely and lets the pin carry the palette.
 */
const pin = (color) =>
  L.divIcon({
    className: '',
    html: `<span style="
      display:block;width:18px;height:18px;border-radius:50% 50% 50% 0;
      background:${color};transform:rotate(-45deg);
      border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)"></span>`,
    iconSize: [18, 18],
    iconAnchor: [9, 18],
    popupAnchor: [0, -16]
  });

const IN_PIN = pin('#0f766e');
const OUT_PIN = pin('#b45309');
const SITE_PIN = pin('#475569');

const PunchLocationMap = ({
  points = [],
  fence = null,
  caption = null,
  height = 260
}) => {
  const usable = useMemo(
    () => points.filter((p) => isUsable(p?.lat, p?.lng)),
    [points]
  );

  if (!usable.length) {
    return (
      <div
        className="flex items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 text-sm text-gray-500"
        style={{ height }}
      >
        No location was recorded for this punch.
      </div>
    );
  }

  const centre = [Number(usable[0].lat), Number(usable[0].lng)];

  return (
    <div className="space-y-2">
      <div className="overflow-hidden rounded-lg border border-gray-200" style={{ height }}>
        <MapContainer
          center={centre}
          zoom={16}
          scrollWheelZoom={false}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {/* The allowed area, drawn first so the pins sit on top of it. */}
          {fence && isUsable(fence.lat, fence.lng) && (
            <>
              <Circle
                center={[Number(fence.lat), Number(fence.lng)]}
                radius={Number(fence.radius_m) || 200}
                pathOptions={{ color: '#0f766e', weight: 1, fillOpacity: 0.06 }}
              />
              <Marker position={[Number(fence.lat), Number(fence.lng)]} icon={SITE_PIN}>
                <Popup>{fence.name ?? 'Site'}</Popup>
              </Marker>
            </>
          )}

          {usable.map((point, index) => (
            <React.Fragment key={`${point.lat},${point.lng},${index}`}>
              {/* Accuracy as a circle rather than a number. "±150 m" means
                  nothing next to a pin that looks exact. */}
              {Number(point.accuracy_m) > 0 && (
                <Circle
                  center={[Number(point.lat), Number(point.lng)]}
                  radius={Number(point.accuracy_m)}
                  pathOptions={{ color: '#94a3b8', weight: 1, fillOpacity: 0.08 }}
                />
              )}
              <Marker
                position={[Number(point.lat), Number(point.lng)]}
                icon={point.kind === 'OUT' ? OUT_PIN : IN_PIN}
              >
                <Popup>
                  <div className="text-xs">
                    <div className="font-medium">{point.label ?? 'Punch'}</div>
                    {point.time && <div className="text-gray-600">{point.time}</div>}
                    {Number(point.accuracy_m) > 0 && (
                      <div className="text-gray-500">
                        Accurate to about {Math.round(Number(point.accuracy_m))} m
                      </div>
                    )}
                    {point.distance_m != null && (
                      <div className="text-gray-500">
                        {Math.round(Number(point.distance_m))} m from the site
                      </div>
                    )}
                  </div>
                </Popup>
              </Marker>
            </React.Fragment>
          ))}
        </MapContainer>
      </div>

      <div className="flex items-start justify-between gap-3">
        {caption ? <p className="text-xs text-gray-500">{caption}</p> : <span />}
        <a
          href={mapsHref(usable[0])}
          target="_blank"
          rel="noreferrer"
          className="flex shrink-0 items-center gap-1 text-xs font-medium text-teal-700 hover:underline"
        >
          Open in Google Maps <ExternalLink size={12} />
        </a>
      </div>
    </div>
  );
};

/**
 * A plain link, for the many places a whole map is too heavy — a register row, a
 * ledger line, a table cell.
 */
export const MapLink = ({ lat, lng, className = '' }) => {
  if (!isUsable(lat, lng)) {
    return <span className="text-gray-400">—</span>;
  }
  return (
    <a
      href={mapsHref({ lat, lng })}
      target="_blank"
      rel="noreferrer"
      className={`inline-flex items-center gap-1 text-teal-700 hover:underline ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      View on map <ExternalLink size={11} />
    </a>
  );
};

export function mapsHref(point) {
  return `https://www.google.com/maps?q=${Number(point.lat)},${Number(point.lng)}`;
}

/**
 * Rejects (0, 0) along with the out-of-range cases. Null Island is in the Gulf of
 * Guinea, and a device that failed to get a fix reports it far more often than
 * anybody is genuinely standing there.
 */
export function isUsable(lat, lng) {
  const a = Number(lat);
  const b = Number(lng);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  if (Math.abs(a) > 90 || Math.abs(b) > 180) return false;
  return !(a === 0 && b === 0);
}

export default PunchLocationMap;
